import React from "react";
import { NavLink, Outlet } from "react-router-dom";

import { LoadState } from "@/lib/scan-ui/screen";
import { cn } from "@/lib/utils";
import { FbsCabinetsCard } from "@/pages/settings/cards/FbsCabinetsCard";
import { FbsOneCRefsCard } from "@/pages/settings/cards/FbsOneCRefsCard";
import { FbsPointsCard } from "@/pages/settings/cards/FbsPointsCard";
import { OzonCabinetsCard } from "@/pages/settings/cards/OzonCabinetsCard";
import { ServiceOneCCard } from "@/pages/settings/cards/ServiceOneCCard";
import { StockSyncCard, StockSyncDiscrepancyCard } from "@/pages/settings/cards/StockSyncCard";
import { StockSyncWarehousesCard } from "@/pages/settings/cards/StockSyncWarehousesCard";
import { SettingsPageHeader } from "@/pages/settings/SettingsPageHeader";
import { useSystemSettingsQuery } from "@/pages/settings/useSystemSettingsQuery";
import { usePortalConfig } from "@/portal/usePortalConfig";

/**
 * «ФБС» — одна секция настроек с вкладками по маркетплейсам.
 *
 * Вкладка есть, только если раздел есть в инсталляции: у Ozon свой раздел
 * (`ozon_fbs`), и у инсталляции может быть ровно один из двух. «Остатки» —
 * общая для обоих: склад 1С и синхронизация остатков не принадлежат маркетплейсу.
 *
 * Вкладки — адреса (`NavLink`), а не состояние компонента: ссылка на
 * конкретную вкладку обязана открываться из закладки и переживать F5.
 */
export function FbsSettingsPage(): React.ReactElement {
  const { hasPage } = usePortalConfig();

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <SettingsPageHeader
        title="ФБС"
        subtitle="Кабинеты продавца, точки сборки, ссылки 1С и синхронизация остатков."
      />
      <div className="flex gap-2 border-b">
        {hasPage("fbs") && <FbsTab to="/settings/fbs/wb">Wildberries</FbsTab>}
        {hasPage("ozon_fbs") && <FbsTab to="/settings/fbs/ozon">Ozon</FbsTab>}
        <FbsTab to="/settings/fbs/stock">Остатки</FbsTab>
      </div>
      <Outlet />
    </div>
  );
}

function FbsTab({ to, children }: { to: string; children: string }): React.ReactElement {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          "-mb-px border-b-2 px-3 py-2 text-sm transition-colors",
          isActive
            ? "border-primary font-medium text-foreground"
            : "border-transparent text-muted-foreground hover:text-foreground",
        )
      }
    >
      {children}
    </NavLink>
  );
}

/** Вкладка Wildberries: кабинеты, точки сборки и ссылки 1С для документов. */
export function FbsWbSettingsTab(): React.ReactElement {
  return (
    <div className="space-y-4">
      <FbsCabinetsCard />
      <FbsPointsCard />
      <FbsOneCRefsCard />
    </div>
  );
}

/** Вкладка Ozon: кабинеты продавца (склад Ozon, метод доставки, реквизиты). */
export function FbsOzonSettingsTab(): React.ReactElement {
  return (
    <div className="space-y-4">
      <OzonCabinetsCard />
    </div>
  );
}

/** Вкладка «Остатки»: синхронизация остатков ФБС. Порядок — общие
 *  переключатели → склады маркетплейсов (включатель, выбор товаров) → сводка
 *  расхождений: сводка бывает длинной и не должна уводить склады за экран.
 *  Карточкам нужны сами настройки — без них показывать выключатель нечему. */
export function FbsStockSettingsTab(): React.ReactElement {
  const { data, isLoading, error, refetch } = useSystemSettingsQuery();

  if (isLoading || error || !data) {
    return (
      <LoadState
        isLoading={isLoading}
        error={error}
        onRetry={() => void refetch()}
        className="flex flex-col items-center gap-3 p-4 text-center"
      />
    );
  }
  return (
    <div className="space-y-4">
      <StockSyncCard settings={data} />
      <ServiceOneCCard />
      <StockSyncWarehousesCard />
      <StockSyncDiscrepancyCard settings={data} />
    </div>
  );
}
